import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { getConciergeThreads, getConciergeThread } from '../../api/noir';
import Loader from '../General/Loader';
import { getSmartImage } from '../../helpers/helpers';
import { getMessageImages } from '../../helpers/concierge_helpers';
import './NoirConversations.scss';

const PAGE_SIZE = 20;

const ThreadList = ({ threads, total, page, onPageChange, onSelectThread, loading }) => {
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className='conversations-list'>
      {loading ? (
        <Loader />
      ) : threads.length === 0 ? (
        <div className='conversations-empty'>No conversations found.</div>
      ) : (
        <>
          <table className='conversations-table'>
            <thead>
              <tr>
                <th>User</th>
                <th>Phone</th>
                <th>Last Message</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {threads.map(thread => (
                <tr key={thread.id} className='conversations-row' onClick={() => onSelectThread(thread.id)}>
                  <td className='cell-user'>
                    <span className='user-name'>{thread.user?.name || '—'}</span>
                    {thread.user?.username && <span className='user-username'>@{thread.user.username}</span>}
                  </td>
                  <td className='cell-phone'>{thread.smsPhoneNumber || '—'}</td>
                  <td className='cell-preview'>{thread.lastMessagePreview || '—'}</td>
                  <td className='cell-time'>{thread.lastMessageAt ? moment(thread.lastMessageAt).fromNow() : '—'}</td>
                  <td className='cell-status'>
                    <span className={cn('status-badge', thread.status)}>{thread.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className='conversations-pagination'>
              <button disabled={page === 0} onClick={() => onPageChange(page - 1)}>
                Previous
              </button>
              <span className='pagination-info'>
                Page {page + 1} of {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ThreadDetail = ({ threadId, promptConfigId, onBack }) => {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getConciergeThread(threadId);
        setThread(data.thread);
        setMessages(data.messages || []);
      } catch {
        cogoToast.error('Failed to load conversation.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [threadId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) return <Loader />;
  if (!thread) return <div className='conversations-empty'>Thread not found.</div>;

  const configIdNum = promptConfigId ? parseInt(promptConfigId, 10) : null;

  return (
    <div className='conversation-detail'>
      <button className='detail-back' onClick={onBack}>
        ← Back to list
      </button>
      <div className='detail-header'>
        <span className='detail-user'>{thread.user?.name || thread.smsPhoneNumber || `Thread #${thread.id}`}</span>
        {thread.user?.username && <span className='detail-username'>@{thread.user.username}</span>}
        {thread.smsPhoneNumber && <span className='detail-phone'>{thread.smsPhoneNumber}</span>}
      </div>
      <div className='detail-phone-frame'>
        <div className='detail-messages'>
          {messages.map(msg => {
            const isHighlighted = configIdNum && msg.ConciergePromptConfig_id === configIdNum;
            const bubbleClass = msg.senderType === 'user' ? 'bubble-user' : msg.senderType === 'system' ? 'bubble-system' : 'bubble-ai';
            const images = getMessageImages(msg.media);
            return (
              <div key={msg.id} className={cn('bubble-wrapper', bubbleClass)}>
                <div className={cn('phone-bubble', bubbleClass, { 'bubble-highlighted': isHighlighted })}>
                  {images.map((m, idx) => (
                    <img key={idx} src={getSmartImage(m.mediaUrl)} alt={m.description || 'Attached image'} className='bubble-image' />
                  ))}
                  {msg.content}
                  {isHighlighted && <span className='config-tag'>Config #{msg.ConciergePromptConfig_id}</span>}
                </div>
                <span className='bubble-timestamp'>{moment(msg.createdAt).format('MMM D, h:mm A')}</span>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

const NoirConversations = () => {
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const promptConfigId = params.get('promptConfigId');
  const threadId = params.get('threadId');
  const pageParam = parseInt(params.get('convPage') || '0', 10);

  const setParam = (key, value) => {
    const next = new URLSearchParams(location.search);
    if (value != null) next.set(key, value);
    else next.delete(key);
    history.replace({ search: next.toString() });
  };

  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getConciergeThreads({
        page: pageParam,
        limit: PAGE_SIZE,
        ConciergePromptConfig_id: promptConfigId || undefined
      });
      setThreads(data.threads || []);
      setTotal(data.total || 0);
    } catch {
      cogoToast.error('Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  }, [pageParam, promptConfigId]);

  useEffect(() => {
    if (!threadId) fetchThreads();
  }, [fetchThreads, threadId]);

  const clearFilter = () => {
    const next = new URLSearchParams(location.search);
    next.delete('promptConfigId');
    next.delete('convPage');
    history.replace({ search: next.toString() });
  };

  const selectThread = id => setParam('threadId', id);

  const goBackToList = () => {
    const next = new URLSearchParams(location.search);
    next.delete('threadId');
    history.replace({ search: next.toString() });
  };

  if (threadId) {
    return <ThreadDetail threadId={threadId} promptConfigId={promptConfigId} onBack={goBackToList} />;
  }

  return (
    <div className='noir-conversations'>
      {promptConfigId && (
        <div className='filter-pill-bar'>
          <span className='filter-pill'>
            Prompt Config #{promptConfigId}
            <button className='filter-pill-x' onClick={clearFilter}>
              ×
            </button>
          </span>
        </div>
      )}
      <ThreadList
        threads={threads}
        total={total}
        page={pageParam}
        onPageChange={p => setParam('convPage', p)}
        onSelectThread={selectThread}
        loading={loading}
      />
    </div>
  );
};

export default NoirConversations;
